import { apiFetchShopper } from "./api";

/* -------------------------
LIST USER EDITS
--------------------------*/

export async function fetchUserEdits() {
  return apiFetchShopper("/shopper/edits");
}

/* -------------------------
SAVE EDIT META
--------------------------*/

export async function saveEditMeta(edit) {
  return apiFetchShopper("/shopper/edits", {
    method: "POST",
    body: JSON.stringify(edit),
  });
}

/* -------------------------
SAVE EDIT ITEMS
--------------------------*/

export async function saveEditItems(editId, items) {
  return apiFetchShopper(`/shopper/edits/${editId}/items`, {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

/* -------------------------
DELETE EDIT
--------------------------*/

export async function deleteEdit(editId) {
  return apiFetchShopper(`/shopper/edits/${editId}`, {
    method: "DELETE",
  });
}

/* -------------------------
EDIT SIGNALS
--------------------------*/

function asArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item == null) return "";
        return String(item).trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    // Handles JSON strings like '["burgundy","cream"]'
    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      const parsed = parseMaybeJson(trimmed, null);
      if (Array.isArray(parsed)) return asArray(parsed);
      return [];
    }

    // Handles comma-separated fallback strings.
    return trimmed
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }

  return [];
}

function parseMaybeJson(value, fallback = null) {
  if (value == null) return fallback;

  if (typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function uniqueArray(values) {
  return Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function normalizeColors(raw) {
  const colors =
    parseMaybeJson(raw?.colors, null) ||
    parseMaybeJson(raw?.colors_json, null) ||
    {};

  const dominant = asArray(
    colors?.dominant ||
      colors?.dominant_colors ||
      colors?.dominant_colours ||
      raw?.dominant_colors ||
      raw?.dominant_colours
  );

  const accents = asArray(
    colors?.accents ||
      colors?.accent_colors ||
      colors?.accent_colours ||
      raw?.accent_colors ||
      raw?.accent_colours
  );

  const all = uniqueArray([
    ...dominant,
    ...accents,
    ...asArray(colors?.all),
    ...asArray(raw?.colors),
    ...asArray(raw?.colours),
  ]);

  return {
    dominant,
    accents,
    all,
  };
}

function normalizeEditSignals(raw) {
  if (!raw) return null;

  const colors = normalizeColors(raw);

  return {
    ...raw,

    colors,

    categories: asArray(
      raw.categories ||
        raw.category_tags ||
        raw.category_tags_json ||
        raw.categories_json
    ),

    silhouettes: asArray(
      raw.silhouettes ||
        raw.silhouette_tags ||
        raw.silhouette_tags_json ||
        raw.silhouetteTags
    ),

    styles: asArray(
      raw.styles ||
        raw.style_tags ||
        raw.style_tags_json ||
        raw.styleTags
    ),

    materials: asArray(
      raw.materials ||
        raw.material_tags ||
        raw.material_tags_json ||
        raw.materialTags
    ),

    intents: asArray(
      raw.intents ||
        raw.intent_tags ||
        raw.intent_tags_json ||
        raw.intentTags
    ),

    phrases: asArray(
      raw.phrases ||
        raw.phrases_json ||
        raw.key_phrases ||
        raw.keyPhrases
    ),

    confidence:
      parseMaybeJson(raw.confidence, null) ||
      parseMaybeJson(raw.confidence_json, null) ||
      {},

    summary:
      raw.summary ||
      raw.ai_summary ||
      raw.description ||
      "",
  };
}

export async function fetchEditSignals(editId) {
  if (!editId) return null;

  const raw = await apiFetchShopper(`/edit-signals/${editId}`);
  return normalizeEditSignals(raw);
}